import React from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Stack,
    Typography
} from '@mui/material';
import { dateOnly, parseLocalDateTimeString, timeOnly } from './utils';

const formatAppointmentRange = (start, end, preferences) => ({
    date: dateOnly(start, preferences),
    time: `${timeOnly(start, preferences)} - ${timeOnly(end, preferences)}`
});

export default function AppointmentChangeConfirmationDialog({
    open,
    change,
    preferences,
    saving = false,
    onCancel,
    onConfirm
}) {
    if (!change) return null;

    const { original, pending, operation } = change;
    const pendingStart = parseLocalDateTimeString(pending.startUtc, preferences.timezone);
    const pendingEnd = parseLocalDateTimeString(pending.endUtc, preferences.timezone);
    const originalRange = formatAppointmentRange(original.startUtc, original.endUtc, preferences);
    const proposedRange = formatAppointmentRange(pendingStart, pendingEnd, preferences);
    const isResize = operation === 'resize-start' || operation === 'resize-end';
    const practitionerChanged = original.practitionerId !== pending.practitionerId;

    return (
        <Dialog
            open={open}
            onClose={saving ? undefined : onCancel}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>{isResize ? 'Confirm appointment resize' : 'Confirm appointment move'}</DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    Review this change before updating {original.patientName || 'the appointment'}.
                </Typography>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    divider={<Divider orientation="vertical" flexItem />}
                    spacing={2}
                >
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="overline" color="text.secondary">Current</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{originalRange.date}</Typography>
                        <Typography variant="body2">{originalRange.time}</Typography>
                        {practitionerChanged && (
                            <Typography variant="body2" sx={{ mt: 1 }}>{change.originalPractitionerName}</Typography>
                        )}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="overline" color="text.secondary">Proposed</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{proposedRange.date}</Typography>
                        <Typography variant="body2">{proposedRange.time}</Typography>
                        {practitionerChanged && (
                            <Typography variant="body2" sx={{ mt: 1 }}>{change.proposedPractitionerName}</Typography>
                        )}
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel} disabled={saving}>Cancel</Button>
                <Button onClick={onConfirm} variant="contained" disabled={saving}>
                    {saving ? 'Updating...' : 'Confirm'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}